<template>
  <div class="resume-page">

    <!-- =====================================================
         GLOBAL LOADER
    ====================================================== -->

    <transition name="loader-fade">
      <div
        v-if="isLoading"
        class="overlay-loader"
      >
        <div class="spinner"></div>
      </div>
    </transition>


    <!-- =====================================================
         HERO
    ====================================================== -->

    <section class="resume-hero">

      <div class="hero-glow hero-glow-blue"></div>
      <div class="hero-glow hero-glow-purple"></div>

      <div class="hero-main">

        <div class="hero-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="5"
              y="3.5"
              width="14"
              height="17"
              rx="3"
              stroke="currentColor"
              stroke-width="1.7"
            />

            <path
              d="M8.5 8H15.5"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <path
              d="M8.5 11.5H15.5"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <path
              d="M8.5 15H12.5"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>
        </div>


        <div class="hero-copy">

          <div class="eyebrow">
            Профессиональный путь
          </div>

          <h2 class="hero-title">
            Ваше резюме
          </h2>

          <p class="hero-description">
            Расскажите о своём опыте, образовании и знании языков.
            Эти данные помогают работодателям лучше понять ваш
            профессиональный уровень.
          </p>

        </div>

      </div>


      <!-- SUMMARY -->

      <div class="resume-summary">

        <div class="summary-item">

          <div class="summary-value">
            {{ career.length }}
          </div>

          <div class="summary-label">
            {{ careerLabel }}
          </div>

        </div>


        <div class="summary-divider"></div>


        <div class="summary-item">

          <div class="summary-value">
            {{ education.length }}
          </div>

          <div class="summary-label">
            {{ educationLabel }}
          </div>

        </div>


        <div class="summary-divider"></div>


        <div class="summary-item">

          <div class="summary-value">
            {{ languages.length }}
          </div>

          <div class="summary-label">
            {{ languagesLabel }}
          </div>

        </div>

      </div>

    </section>


    <!-- =====================================================
         CAREER
    ====================================================== -->

    <section class="resume-section">

      <div class="section-header">

        <div class="section-header-left">

          <div class="section-icon career-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M8 7V5.5C8 4.67 8.67 4 9.5 4H14.5C15.33 4 16 4.67 16 5.5V7"
                stroke="currentColor"
                stroke-width="1.6"
              />

              <rect
                x="4"
                y="7"
                width="16"
                height="12"
                rx="2.5"
                stroke="currentColor"
                stroke-width="1.6"
              />

              <path
                d="M4 11.5H20"
                stroke="currentColor"
                stroke-width="1.6"
              />

              <path
                d="M10 11.5V13H14V11.5"
                stroke="currentColor"
                stroke-width="1.6"
              />
            </svg>
          </div>


          <div>

            <div class="section-eyebrow">
              Опыт работы
            </div>

            <h3 class="section-title">
              Карьера
            </h3>

            <p class="section-description">
              Добавьте компании и позиции, на которых вы работали.
            </p>

          </div>

        </div>


        <div class="section-count">
          {{ career.length }}
        </div>

      </div>


      <div
        v-if="career.length"
        class="resume-list"
      >
      <ResumeCard
        v-for="(item, index) in career"
        :key="item.id"
        type="career"
        :title="item.company"
        :subtitle="item.specialty"
        :period="formatCareerPeriod(item)"
        :is-last="index === career.length - 1"
        :is-deleting="deletingCareerId === item.id"
        @delete="deleteCareer(item.id)"
      />
      </div>


      <div
        v-else
        class="empty-state"
      >

        <div class="empty-icon career-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M8 7V5.5C8 4.67 8.67 4 9.5 4H14.5C15.33 4 16 4.67 16 5.5V7"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <rect
              x="4"
              y="7"
              width="16"
              height="12"
              rx="2.5"
              stroke="currentColor"
              stroke-width="1.6"
            />
          </svg>
        </div>

        <div>
          <div class="empty-title">
            Карьера пока не добавлена
          </div>

          <div class="empty-description">
            Добавьте первое место работы, чтобы начать формировать резюме.
          </div>
        </div>

      </div>


      <div class="add-area">
        <button
          type="button"
          class="add-button"
          @click="addCareer"
        >
          <span class="add-button-icon">+</span>

          <span>
            Добавить место работы
          </span>
        </button>
      </div>

    </section>


    <!-- =====================================================
         EDUCATION
    ====================================================== -->

    <section class="resume-section">

      <div class="section-header">

        <div class="section-header-left">

          <div class="section-icon education-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M3.5 9L12 4.5L20.5 9L12 13.5L3.5 9Z"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linejoin="round"
              />

              <path
                d="M7 11V15.5C9.7 17.4 14.3 17.4 17 15.5V11"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />

              <path
                d="M20.5 9V14"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </div>


          <div>

            <div class="section-eyebrow">
              Обучение
            </div>

            <h3 class="section-title">
              Образование
            </h3>

            <p class="section-description">
              Университеты, факультеты и другая профессиональная подготовка.
            </p>

          </div>

        </div>


        <div class="section-count">
          {{ education.length }}
        </div>

      </div>


      <div
        v-if="education.length"
        class="resume-list"
      >
      <ResumeCard
        v-for="(item, index) in education"
        :key="item.id"
        type="education"
        :title="item.university"
        :subtitle="item.faculty"
        :period="formatEducationPeriod(item)"
        :is-last="index === education.length - 1"
        :is-deleting="deletingEducationId === item.id"
        @delete="deleteEducation(item.id)"
      />
      </div>


      <div
        v-else
        class="empty-state"
      >

        <div class="empty-icon education-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M3.5 9L12 4.5L20.5 9L12 13.5L3.5 9Z"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linejoin="round"
            />

            <path
              d="M7 11V15.5C9.7 17.4 14.3 17.4 17 15.5V11"
              stroke="currentColor"
              stroke-width="1.6"
            />
          </svg>
        </div>

        <div>
          <div class="empty-title">
            Образование пока не добавлено
          </div>

          <div class="empty-description">
            Расскажите об университете или другой профессиональной подготовке.
          </div>
        </div>

      </div>


      <div class="add-area">
        <button
          type="button"
          class="add-button"
          @click="addEducation"
        >
          <span class="add-button-icon">+</span>

          <span>
            Добавить образование
          </span>
        </button>
      </div>

    </section>


    <!-- =====================================================
         LANGUAGES
    ====================================================== -->

    <section class="resume-section">

      <div class="section-header">

        <div class="section-header-left">

          <div class="section-icon language-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="8"
                stroke="currentColor"
                stroke-width="1.6"
              />

              <path
                d="M4.5 12H19.5"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />

              <path
                d="M12 4C14 6.2 15 8.9 15 12C15 15.1 14 17.8 12 20"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />

              <path
                d="M12 4C10 6.2 9 8.9 9 12C9 15.1 10 17.8 12 20"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </div>


          <div>

            <div class="section-eyebrow">
              Коммуникация
            </div>

            <h3 class="section-title">
              Языки
            </h3>

            <p class="section-description">
              Укажите языки, которыми владеете, и уровень знания.
            </p>

          </div>

        </div>


        <div class="section-count">
          {{ languages.length }}
        </div>

      </div>


      <div
        v-if="languages.length"
        class="resume-list"
      >
      <ResumeCard
        v-for="(lang, index) in languages"
        :key="lang.id"
        type="language"
        :title="lang.language"
        :subtitle="lang.language_level"
        :is-last="index === languages.length - 1"
        :is-deleting="deletingLanguageId === lang.id"
        @delete="deleteLanguage(lang.id)"
      />
      </div>


      <div
        v-else
        class="empty-state"
      >

        <div class="empty-icon language-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="8"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <path
              d="M4.5 12H19.5"
              stroke="currentColor"
              stroke-width="1.6"
            />
          </svg>
        </div>

        <div>
          <div class="empty-title">
            Языки пока не добавлены
          </div>

          <div class="empty-description">
            Добавьте языки, которыми вы можете пользоваться в работе.
          </div>
        </div>

      </div>


      <div class="add-area">
        <button
          type="button"
          class="add-button"
          @click="addLanguage"
        >
          <span class="add-button-icon">+</span>

          <span>
            Добавить язык
          </span>
        </button>
      </div>

    </section>


    <!-- =====================================================
         DRAWERS
    ====================================================== -->

    <ProfileCareerDrawer
      v-if="showCareerDrawer"
      :visible="showCareerDrawer"
      :user-id="userId"
      @close="showCareerDrawer = false"
      @save="onCareerSave"
      @snackbar="({ message, type }) => handleSnackbar(message, type)"
    />


    <ProfileEducationDrawer
      :visible="showEducationDrawer"
      @close="showEducationDrawer = false"
      @save="onEducationSave"
      @snackbar="({ message, type }) => handleSnackbar(message, type)"
    />


    <ProfileLanguageDrawer
      :visible="showLanguageDrawer"
      :existing-languages="languages"
      @close="showLanguageDrawer = false"
      @save="onLanguageSave"
      @snackbar="({ message, type }) => handleSnackbar(message, type)"
    />

  </div>


  <Snackbar
    :message="snackbarMessage"
    :type="snackbarType"
    :show="showSnackbar"
  />

</template>


<script setup>

import {
  computed,
  ref,
  watch
} from "vue";

import SummaryService
  from "../../api/SummaryService";

import ResumeCard
  from "../../components/profile/ResumeCard.vue";

import ProfileCareerDrawer
  from "../../components/profile/ProfileCareerDrawer.vue";

import ProfileEducationDrawer
  from "../../components/profile/ProfileEducationDrawer.vue";

import ProfileLanguageDrawer
  from "../../components/profile/ProfileLanguageDrawer.vue";

import Snackbar
  from "../../components/Snackbar.vue";


/* ============================================================
   PROPS
============================================================ */

const props = defineProps({

  userId: String,

  career: {
    type: Array,
    default: () => []
  },

  education: {
    type: Array,
    default: () => []
  },

  languages: {
    type: Array,
    default: () => []
  }

});


/* ============================================================
   DATA
============================================================ */

const career =
  ref([...props.career]);

const education =
  ref([...props.education]);

const languages =
  ref([...props.languages]);


const isLoading =
  ref(false);


const deletingCareerId =
  ref(null);

const deletingEducationId =
  ref(null);

const deletingLanguageId =
  ref(null);


const showCareerDrawer =
  ref(false);

const showEducationDrawer =
  ref(false);

const showLanguageDrawer =
  ref(false);


/* ============================================================
   SNACKBAR
============================================================ */

const showSnackbar =
  ref(false);

const snackbarMessage =
  ref("");

const snackbarType =
  ref("success");


let snackbarTimer =
  null;


function handleSnackbar(
  message,
  type = "success"
) {

  snackbarMessage.value =
    message;

  snackbarType.value =
    type;

  showSnackbar.value =
    true;


  if (snackbarTimer) {
    clearTimeout(snackbarTimer);
  }


  snackbarTimer =
    setTimeout(() => {

      showSnackbar.value =
        false;

    }, 3000);

}


/* ============================================================
   WATCH PROPS
============================================================ */

watch(
  () => props.career,
  value => {
    career.value = [
      ...(value || [])
    ];
  },
  {
    deep: true
  }
);


watch(
  () => props.education,
  value => {
    education.value = [
      ...(value || [])
    ];
  },
  {
    deep: true
  }
);


watch(
  () => props.languages,
  value => {
    languages.value = [
      ...(value || [])
    ];
  },
  {
    deep: true
  }
);


/* ============================================================
   COUNTS
============================================================ */

const careerLabel =
  computed(() =>
    pluralize(
      career.value.length,
      [
        "место работы",
        "места работы",
        "мест работы"
      ]
    )
  );


const educationLabel =
  computed(() =>
    pluralize(
      education.value.length,
      [
        "образование",
        "образования",
        "образований"
      ]
    )
  );


const languagesLabel =
  computed(() =>
    pluralize(
      languages.value.length,
      [
        "язык",
        "языка",
        "языков"
      ]
    )
  );


function pluralize(
  count,
  words
) {

  const mod100 =
    count % 100;

  const mod10 =
    count % 10;


  if (
    mod100 >= 11
    && mod100 <= 19
  ) {
    return words[2];
  }


  if (mod10 === 1) {
    return words[0];
  }


  if (
    mod10 >= 2
    && mod10 <= 4
  ) {
    return words[1];
  }


  return words[2];

}


/* ============================================================
   PERIODS
============================================================ */

function formatCareerPeriod(item) {

  const start =
    item?.start_date || "";

  const end =
    item?.end_date || "";


  if (
    start
    && end
  ) {
    return `${start} – ${end}`;
  }


  if (start) {
    return `С ${start}`;
  }


  if (end) {
    return `До ${end}`;
  }


  return "";

}


function formatEducationPeriod(item) {

  if (!item?.end_date) {
    return "";
  }


  return `Окончил в ${item.end_date}`;

}


/* ============================================================
   DELETE CAREER
============================================================ */

async function deleteCareer(id) {

  if (
    !id
    || deletingCareerId.value
  ) {
    return;
  }


  deletingCareerId.value =
    id;


  try {

    await SummaryService.deleteCareer(
      id
    );


    career.value =
      career.value.filter(
        item =>
          item.id !== id
      );


    handleSnackbar(
      "Карьера удалена"
    );


  } catch (error) {

    console.error(
      "❌ Ошибка при удалении карьеры:",
      error
    );


    handleSnackbar(
      "Не удалось удалить",
      "error"
    );


  } finally {

    deletingCareerId.value =
      null;

  }

}


/* ============================================================
   DELETE EDUCATION
============================================================ */

async function deleteEducation(id) {

  if (
    !id
    || deletingEducationId.value
  ) {
    return;
  }


  deletingEducationId.value =
    id;


  try {

    await SummaryService.deleteEducation(
      id
    );


    education.value =
      education.value.filter(
        item =>
          item.id !== id
      );


    handleSnackbar(
      "Образование удалено"
    );


  } catch (error) {

    console.error(
      "❌ Ошибка при удалении образования:",
      error
    );


    handleSnackbar(
      "Не удалось удалить",
      "error"
    );


  } finally {

    deletingEducationId.value =
      null;

  }

}


/* ============================================================
   DELETE LANGUAGE
============================================================ */

async function deleteLanguage(id) {

  if (
    !id
    || deletingLanguageId.value
  ) {
    return;
  }


  deletingLanguageId.value =
    id;


  try {

    await SummaryService.deleteLanguage(
      id
    );


    languages.value =
      languages.value.filter(
        item =>
          item.id !== id
      );


    handleSnackbar(
      "Язык удалён"
    );


  } catch (error) {

    console.error(
      "❌ Ошибка при удалении языка:",
      error
    );


    handleSnackbar(
      "Не удалось удалить",
      "error"
    );


  } finally {

    deletingLanguageId.value =
      null;

  }

}


/* ============================================================
   SAVED ITEMS
============================================================ */

function onEducationSave(
  savedEducation
) {

  if (!savedEducation) {
    return;
  }


  education.value = [
    savedEducation,
    ...education.value
  ];

}


function onCareerSave(
  savedCareer
) {

  if (!savedCareer) {
    return;
  }


  career.value = [
    savedCareer,
    ...career.value
  ];

}


function onLanguageSave(
  savedLanguage
) {

  if (!savedLanguage) {
    return;
  }


  languages.value = [
    savedLanguage,
    ...languages.value
  ];

}


/* ============================================================
   OPEN DRAWERS
============================================================ */

function addCareer() {
  showCareerDrawer.value =
    true;
}


function addEducation() {
  showEducationDrawer.value =
    true;
}


function addLanguage() {
  showLanguageDrawer.value =
    true;
}

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.resume-page {
  width: 100%;

  display: flex;
  flex-direction: column;

  gap: 16px;

  font-family:
    "Eastman-Regular",
    sans-serif;
}


/* ============================================================
   BASE CARD
============================================================ */

.resume-hero,
.resume-section {
  position: relative;

  width: 100%;

  box-sizing: border-box;

  overflow: hidden;

  background: #ffffff;

  border:
    1px solid
    rgba(20, 35, 60, 0.09);

  border-radius: 24px;

  box-shadow:
    0 16px 45px
    rgba(15, 23, 42, 0.055);
}


/* ============================================================
   HERO
============================================================ */

.resume-hero {
  padding: 28px;
}


.hero-main {
  position: relative;

  z-index: 2;

  display: flex;
  align-items: flex-start;

  gap: 15px;
}


.hero-icon {
  width: 48px;
  height: 48px;

  flex:
    0 0 48px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #407edc;

  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, 0.12),
      rgba(124, 92, 231, 0.075)
    );

  border:
    1px solid
    rgba(40, 120, 230, 0.11);

  border-radius: 15px;
}


.hero-icon svg {
  width: 23px;
  height: 23px;
}


.hero-copy {
  min-width: 0;
}


.eyebrow,
.section-eyebrow {
  margin-bottom: 5px;

  color: #9499a2;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1.2;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.hero-title {
  margin: 0;

  color: #292e35;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 17px;

  line-height: 1.3;

  letter-spacing: -0.25px;
}


.hero-description {
  max-width: 570px;

  margin:
    6px
    0
    0;

  color: #80858e;

  font-size: 11px;

  line-height: 1.5;
}


/* ============================================================
   HERO GLOW
============================================================ */

.hero-glow {
  position: absolute;

  width: 230px;
  height: 230px;

  pointer-events: none;

  border-radius: 50%;

  filter: blur(80px);

  opacity: 0.1;
}


.hero-glow-blue {
  top: -125px;
  right: -65px;

  background: #2887f7;
}


.hero-glow-purple {
  bottom: -170px;
  left: -100px;

  background: #7c5ce7;
}


/* ============================================================
   SUMMARY
============================================================ */

.resume-summary {
  position: relative;

  z-index: 2;

  display: flex;
  align-items: center;

  margin-top: 25px;

  padding: 15px 17px;

  background:
    rgba(247, 249, 252, 0.78);

  border:
    1px solid
    rgba(25, 40, 60, 0.06);

  border-radius: 15px;
}


.summary-item {
  min-width: 0;

  flex: 1;

  display: flex;
  align-items: baseline;

  gap: 6px;
}


.summary-value {
  color: #3279dc;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 15px;
}


.summary-label {
  color: #8f959e;

  font-size: 8px;

  line-height: 1.3;
}


.summary-divider {
  width: 1px;
  height: 24px;

  margin: 0 18px;

  background:
    rgba(25, 40, 60, 0.07);
}


/* ============================================================
   SECTIONS
============================================================ */

.resume-section {
  padding:
    25px
    20px
    16px;
}


.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 20px;

  padding:
    0
    8px
    20px;
}


.section-header-left {
  display: flex;
  align-items: flex-start;

  gap: 13px;

  min-width: 0;
}


.section-icon,
.empty-icon {
  width: 40px;
  height: 40px;

  flex:
    0 0 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 12px;
}


.section-icon svg,
.empty-icon svg {
  width: 19px;
  height: 19px;
}


.career-icon {
  color: #477ed7;

  background:
    rgba(58, 121, 220, 0.075);
}


.education-icon {
  color: #725ed0;

  background:
    rgba(112, 89, 209, 0.075);
}


.language-icon {
  color: #349172;

  background:
    rgba(49, 159, 116, 0.075);
}


.section-title {
  margin: 0;

  color: #30353d;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 14px;

  font-weight: 500;
}


.section-description {
  max-width: 520px;

  margin:
    5px
    0
    0;

  color: #999ea6;

  font-size: 9px;

  line-height: 1.4;
}


.section-count {
  min-width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0 8px;

  box-sizing: border-box;

  color: #848a93;

  background:
    rgba(30, 40, 55, 0.045);

  border:
    1px solid
    rgba(30, 40, 55, 0.055);

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


/* ============================================================
   LIST
============================================================ */

.resume-list {
  position: relative;

  padding:
    0
    8px;
}


/* ============================================================
   EMPTY
============================================================ */

.empty-state {
  display: flex;
  align-items: center;

  gap: 12px;

  margin:
    0
    8px;

  padding: 15px;

  background:
    #f8f9fb;

  border:
    1px dashed
    rgba(30, 45, 65, 0.1);

  border-radius: 14px;
}


.empty-title {
  color: #555b64;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.empty-description {
  max-width: 480px;

  margin-top: 3px;

  color: #9ba0a8;

  font-size: 8px;

  line-height: 1.4;
}


/* ============================================================
   ADD
============================================================ */

.add-area {
  margin-top: 13px;

  padding:
    14px
    8px
    0;

  border-top:
    1px solid
    rgba(25, 40, 60, 0.06);
}


.add-button {
  min-height: 38px;

  display: inline-flex;
  align-items: center;

  gap: 8px;

  padding:
    0
    13px;

  color: #397bdc;

  background:
    rgba(40, 135, 247, 0.06);

  border:
    1px solid
    rgba(40, 135, 247, 0.09);

  border-radius: 11px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}


.add-button:hover {
  background:
    rgba(40, 135, 247, 0.1);

  transform:
    translateY(-1px);

  box-shadow:
    0 6px 15px
    rgba(40, 135, 247, 0.1);
}


.add-button:active {
  transform:
    translateY(0);
}


.add-button-icon {
  width: 20px;
  height: 20px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background: #3884ec;

  border-radius: 6px;

  font-family: Arial, sans-serif;

  font-size: 16px;
  font-weight: 300;

  line-height: 1;
}


/* ============================================================
   OVERLAY
============================================================ */

.overlay-loader {
  position: fixed;

  inset: 0;

  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    rgba(245, 247, 250, 0.64);

  backdrop-filter:
    blur(5px);
}


.spinner {
  width: 36px;
  height: 36px;

  border:
    3px solid
    rgba(40, 135, 247, 0.16);

  border-top-color:
    #2887f7;

  border-radius: 50%;

  animation:
    spin
    0.7s
    linear
    infinite;
}


@keyframes spin {
  to {
    transform:
      rotate(360deg);
  }
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.resume-hero,
html[data-theme="dark"]
.resume-section {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, 0.07);

  box-shadow:
    0 18px 50px
    rgba(0, 0, 0, 0.24);
}


html[data-theme="dark"]
.hero-title,
html[data-theme="dark"]
.section-title {
  color: #f0f1f3;
}


html[data-theme="dark"]
.hero-description,
html[data-theme="dark"]
.section-description {
  color: #8d929b;
}


html[data-theme="dark"]
.resume-summary {
  background:
    rgba(255, 255, 255, 0.035);

  border-color:
    rgba(255, 255, 255, 0.055);
}


html[data-theme="dark"]
.summary-label {
  color: #898e97;
}


html[data-theme="dark"]
.summary-divider {
  background:
    rgba(255, 255, 255, 0.065);
}


html[data-theme="dark"]
.section-count {
  color: #9fa4ad;

  background:
    rgba(255, 255, 255, 0.045);

  border-color:
    rgba(255, 255, 255, 0.06);
}


html[data-theme="dark"]
.empty-state {
  background:
    rgba(255, 255, 255, 0.03);

  border-color:
    rgba(255, 255, 255, 0.08);
}


html[data-theme="dark"]
.empty-title {
  color: #d7d9dd;
}


html[data-theme="dark"]
.empty-description {
  color: #818690;
}


html[data-theme="dark"]
.add-area {
  border-color:
    rgba(255, 255, 255, 0.06);
}


html[data-theme="dark"]
.add-button {
  color: #8abaff;

  background:
    rgba(40, 135, 247, 0.09);

  border-color:
    rgba(40, 135, 247, 0.13);
}


html[data-theme="dark"]
.overlay-loader {
  background:
    rgba(15, 16, 19, 0.7);
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 700px) {

  .resume-page {
    gap: 13px;
  }


  .resume-hero {
    padding: 20px;

    border-radius: 20px;
  }


  .resume-section {
    padding:
      21px
      12px
      14px;

    border-radius: 20px;
  }


  .resume-summary {
    padding:
      13px
      12px;
  }


  .summary-item {
    flex-direction: column;

    gap: 2px;
  }


  .summary-divider {
    margin:
      0
      10px;
  }


  .section-header {
    padding:
      0
      6px
      17px;
  }


  .resume-list {
    padding:
      0
      5px;
  }


  .empty-state {
    margin:
      0
      5px;
  }


  .add-area {
    padding-left: 5px;
    padding-right: 5px;
  }


  .add-button {
    width: 100%;

    justify-content: center;

    min-height: 41px;
  }

}


/* ============================================================
   TRANSITION
============================================================ */

.loader-fade-enter-active,
.loader-fade-leave-active {
  transition:
    opacity
    0.2s ease;
}


.loader-fade-enter-from,
.loader-fade-leave-to {
  opacity: 0;
}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .add-button,
  .loader-fade-enter-active,
  .loader-fade-leave-active {
    transition: none;
  }


  .spinner {
    animation: none;
  }

}

</style>